/**
 * WebRTC audio quality configuration for Telnyx calls.
 * Centralized so outbound, inbound, and barge-in components use identical settings.
 *
 * Audio pipeline (layered noise reduction):
 *   Mic → getUserMedia (voiceIsolation + echoCancellation) → RNNoise WASM filter (Jitsi)
 *       → TelnyxRTC (HD OPUS codec)
 *       → Telnyx server-side noise suppression
 *       → Remote party
 */
import { createFilteredStream, isRNNoiseSupported } from './rnnoiseFilter';

/**
 * Build getUserMedia audio constraints optimized for voice calls.
 * @param {string|null} microphoneId - Preferred microphone device ID
 * @returns {MediaTrackConstraints}
 */
export function getVoiceAudioConstraints(microphoneId = null) {
  return {
    ...(microphoneId ? { deviceId: { exact: microphoneId } } : {}),
    echoCancellation: true,
    noiseSuppression: true,
    autoGainControl: true,
    sampleRate: 48000,
    channelCount: 1,
    // AI-powered voice isolation — removes room reverb, background noise, and
    // the "speakerphone" sound. Chrome 113+; silently ignored on older browsers.
    voiceIsolation: true,
  };
}

/**
 * Get preferred OPUS codec configured for HD voice quality.
 * Returns preferred_codecs array for Telnyx newCall() options,
 * or undefined if OPUS is unavailable.
 */
export function getHDVoiceCodecs() {
  try {
    const capabilities = RTCRtpReceiver.getCapabilities('audio');
    if (!capabilities?.codecs) return undefined;

    const opusCodecs = capabilities.codecs.filter(
      c => c.mimeType.toLowerCase() === 'audio/opus'
    );
    if (opusCodecs.length === 0) return undefined;

    // Configure OPUS for HD voice:
    // - maxaveragebitrate=48000: 48kbps (default ~32kbps) — noticeably clearer
    // - useinbandfec=1: Forward Error Correction — graceful packet loss handling
    // - minptime=10: Low packet time — reduces latency
    // - stereo=0, sprop-stereo=0: Mono — optimized for single-speaker voice
    return opusCodecs.map(c => ({
      ...c,
      sdpFmtpLine: 'minptime=10;useinbandfec=1;maxaveragebitrate=48000;stereo=0;sprop-stereo=0',
    }));
  } catch (e) {
    console.log('Could not configure HD voice codecs:', e);
    return undefined;
  }
}

/**
 * Get a noise-filtered microphone MediaStream for use with Telnyx calls.
 *
 * @param {string|null} microphoneId - Preferred microphone device ID
 * @param {boolean} rnnoiseEnabled - Whether to apply RNNoise filter (from user prefs)
 * @returns {Promise<{ stream: MediaStream, cleanup: Function, filterType: string } | null>}
 *   Returns { stream, cleanup, filterType } if a filter was applied, or null if skipped/unavailable.
 *   When non-null, pass stream as `localStream` to Telnyx and call cleanup() on hangup.
 */
export async function getNoiseFilteredStream(microphoneId = null, rnnoiseEnabled = true) {
  // RNNoise (Jitsi)
  if (!rnnoiseEnabled || !isRNNoiseSupported()) return null;

  try {
    const constraints = getVoiceAudioConstraints(microphoneId);
    const rawStream = await navigator.mediaDevices.getUserMedia({ audio: constraints });
    const filtered = await createFilteredStream(rawStream);

    const rawTracks = rawStream.getTracks();
    const originalCleanup = filtered.cleanup;

    return {
      stream: filtered.stream,
      filterType: 'rnnoise',
      cleanup: () => {
        originalCleanup();
        rawTracks.forEach(t => t.stop());
      },
    };
  } catch (e) {
    console.warn('RNNoise filter setup failed, falling back to native audio:', e);
    return null;
  }
}
